import { ReactNode, useRef } from 'react';
import { Popover } from 'react-aria-components';
import styled from 'styled-components';
import { colors, rgbaColors } from '@karnott/colors';
import { fontFamily, pixelSpacing, spacing, zIndex } from '@karnott/theme';

const Container = styled.div`
  position: relative;
  min-width: 0;
`;

const Menu = styled.div`
  font-family: ${fontFamily()};
  background-color: ${colors('white')};
  z-index: ${zIndex('sky')};
  color: ${colors('black')};
  box-shadow: 0px 0px 10px ${rgbaColors('black', 600, 0.1)};
  border-radius: ${pixelSpacing('xSmall')};
`;

const MenuContent = styled.div`
  display: flex;
`;

type Props = {
  /** Container of the popover */
  children: ReactNode;
  /** Content of the popover */
  content: ReactNode;
  /** Should the popover take the full width of the container */
  full?: boolean;
  /** Is the popover opened */
  opened: boolean;
  /** Should the popover be placed above the container */
  top?: boolean;
  /** Should the right edge of the popover be aligned with the right edge of the container */
  right?: boolean;
  /** Should the left edge of the popover be aligned with the left edge of the container */
  left?: boolean;
  /** Function to close the popover */
  onRequestClose: () => void;
};

/** A controlled popover menu that can contain anything */
export function PopoverMenu({ full = false, children, content, opened, top, right, left, onRequestClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  const triggerWidth = ref.current?.offsetWidth ?? 0;

  return (
    <Container ref={ref} data-testid="karnott-sdk-popover-menu">
      {children}
      <Popover
        isOpen={opened}
        onOpenChange={(isOpen) => !isOpen && onRequestClose()}
        triggerRef={ref}
        placement={`${top ? 'top' : 'bottom'}${right ? ' end' : left ? ' start' : ''}`}
        offset={spacing('xSmall')}
        style={{ width: full ? triggerWidth : undefined }}
      >
        <Menu onClick={onRequestClose}>
          <MenuContent>{content}</MenuContent>
        </Menu>
      </Popover>
    </Container>
  );
}
